import {Button, Form, Modal, Spinner} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
// import Select from 'react-select'
import {useEffect, useState} from 'react'

import {useMutation, useQuery} from '@apollo/client'
import {ADD_NOTE_POLICY, GET_NOTES_POLICY} from '../../../gql/mutations/policyMutation'
import {GET_ALL_USERS_LIST} from '../../../gql/queries/usersQuery'
import {toast} from 'react-toastify'
import {ContentNotes} from './notes/ContentNotes'
import {IResNotePolicy} from '../../../types/policiesApi'
import {MentionInput} from '../../../components/MentionInput'

const NOTES_MODAL_STYLES = `
        .notes-container .note-content img {
          max-width: 100% !important;
          width: auto !important;
          height: auto !important;
          display: block;
        }
        .notes-container .alert {
          max-width: 100%;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
      `

interface IResAddNotePolicy {
  createPolicyNote: {success: boolean; errors?: {message: string}[]}
}

interface IUserMentionCandidate {
  id: string
  firstName: string
  lastName: string
}

const normalizeWhitespace = (value: string) => value.replace(/\s+/g, ' ').trim()

const normalizeMentionToken = (value: string) => normalizeWhitespace(value).toLowerCase()

const getMentionText = (user: IUserMentionCandidate) =>
  `@${normalizeWhitespace(`${user.firstName} ${user.lastName}`)}`

const stripHtmlToText = (html: string) => {
  if (typeof window === 'undefined') return html

  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  return doc.body.textContent || ''
}

const extractStyledMentionTexts = (html: string) => {
  if (typeof window === 'undefined' || !html) return []

  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const mentionTexts = new Set<string>()

  doc.querySelectorAll('strong').forEach((node) => {
    const rawText = node.textContent?.replace(/\uFEFF/g, ' ').trim() || ''
    if (!rawText.startsWith('@')) return

    const normalizedText = normalizeWhitespace(rawText)
    if (normalizedText.length > 1) {
      mentionTexts.add(normalizedText)
    }
  })

  return Array.from(mentionTexts)
}

const resolveMentionedUserIdsFromContent = (html: string, usersList?: IUserMentionCandidate[]) => {
  if (!html || !usersList?.length) return []

  const htmlContent = normalizeMentionToken(html.replace(/\uFEFF/g, ' '))
  const textContent = normalizeMentionToken(stripHtmlToText(html).replace(/\uFEFF/g, ' '))
  const mentionedIds = new Set<string>()

  usersList.forEach((user) => {
    const mentionText = normalizeMentionToken(getMentionText(user))
    if (mentionText === '@') return

    if (htmlContent.includes(mentionText) || textContent.includes(mentionText)) {
      mentionedIds.add(user.id)
    }
  })

  return Array.from(mentionedIds)
}

interface IModalProps {
  isOpen: boolean
  onClose: () => void
  idPolicy: string
  onRefetchNotes?: () => void
}

export const ModalNotes: React.FC<IModalProps> = ({isOpen, onClose, idPolicy, onRefetchNotes}) => {
  const intl = useIntl()
  const errMsg = (message: string) =>
    intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message})
  const {
    data: dataNotes,
    loading: loadingNotes,
    error: errorNotes,
    refetch,
  } = useQuery<IResNotePolicy>(GET_NOTES_POLICY, {
    variables: {policyId: idPolicy},
    fetchPolicy: 'no-cache',
  })
  const {data: usersData} = useQuery<{allUsersList: IUserMentionCandidate[]}>(GET_ALL_USERS_LIST, {
    fetchPolicy: 'cache-first',
  })
  const [gqlAddNote, {data, loading, error}] = useMutation<IResAddNotePolicy>(ADD_NOTE_POLICY)

  const [value, setValue] = useState('')
  const [urgent, setUrgent] = useState(false)
  const [mentionedUserIds, setMentionedUserIds] = useState<string[]>([])
  // const [newStatus, setNewStatus] = useState('')

  useEffect(() => {
    if (error) toast.error(errMsg(error.message))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])
  useEffect(() => {
    if (errorNotes) toast.error(errMsg(errorNotes.message))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorNotes])

  useEffect(() => {
    if (!data) return
    if (data.createPolicyNote.success) {
      refetch()
      toast.success(
        intl.formatMessage({
          id: 'policies.detail.notes.toast.saved',
          defaultMessage: 'Note saved successfully',
        })
      )
      onRefetchNotes?.()
      setValue('')
      setUrgent(false)
      setMentionedUserIds([])
    } else if (data.createPolicyNote.errors && data.createPolicyNote.errors.length > 0) {
      toast.error(errMsg(data.createPolicyNote.errors[0].message))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const handleComment = () => {
    if (!value || !idPolicy) return

    const styledMentionTexts = extractStyledMentionTexts(value)
    const hasMentionSyntax = styledMentionTexts.length > 0

    if (hasMentionSyntax && !usersData?.allUsersList?.length) {
      toast.error(
        intl.formatMessage({
          id: 'policies.detail.notes.toast.usersLoading',
          defaultMessage: 'Users are still loading. Please wait a moment and try again.',
        })
      )
      return
    }

    const resolvedMentionedUserIds = resolveMentionedUserIdsFromContent(value, usersData?.allUsersList)
    const finalMentionedUserIds =
      resolvedMentionedUserIds.length > 0 ? resolvedMentionedUserIds : mentionedUserIds

    console.log('[Policy Notes] mentionedUserIds state:', mentionedUserIds)
    console.log('[Policy Notes] mentionedUserIds resolved on save:', resolvedMentionedUserIds)

    if (hasMentionSyntax && finalMentionedUserIds.length === 0) {
      toast.error(
        intl.formatMessage({
          id: 'policies.detail.notes.toast.mentionUnresolved',
          defaultMessage:
            'A mentioned user could not be resolved. Please select the user from the @ dropdown before saving.',
        })
      )
      return
    }

    const policyNoteData = {
      comments: value,
      policyId: idPolicy,
      urgent,
      mentionedUserIds: finalMentionedUserIds, // Array of user IDs that were mentioned with @
    }
    console.log('policyNoteData', policyNoteData)
    gqlAddNote({
      variables: {
        policyNoteData,
      },
    })
  }
  return (
    <>
      <style>{NOTES_MODAL_STYLES}</style>
      <Modal show={isOpen} onHide={onClose} size='xl'>
        <Modal.Header closeButton>
        <Modal.Title className='d-flex align-items-center gap-x-2'>
          <FormattedMessage id='policies.detail.notes.modal.title' defaultMessage='Policy notes' /> {loadingNotes || !dataNotes ? (
            <Spinner animation='border' size='sm' />
          ) : (
            <FormattedMessage
              id='policies.detail.notes.modal.count'
              defaultMessage='({count})'
              values={{count: dataNotes.policyNotesByPolicy.length}}
            />
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '380px',
              maxHeight: '380px',
              overflowY: 'auto',
              overflowX: 'hidden',
              backgroundColor: '#f8f8fa',
            }}
            className='px-3 py-2 border notes-container'
          >
            {loading || !dataNotes ? (
              <Spinner animation='border' />
            ) : (
              <ContentNotes notes={dataNotes.policyNotesByPolicy} />
            )}
          </div>
          {/* <Row>
            <Col md={6}>
              <div>
                <label>Who should be notified?</label>
                <Select />
                <span className='text-muted'>
                  If you leave this field empty, no notifications will be sent.
                </span>
              </div>
            </Col>
            <Col md={6}>
              <div>
                <label>Change the policy status: (optional)</label>
                <Select />
              </div>
            </Col>
          </Row> */}
          <div className='mb-3 mt-4'>
            <MentionInput
              placeholder={intl.formatMessage({
                id: 'policies.detail.notes.placeholder',
                defaultMessage: 'Type your comments here... Use @ to mention users',
              })}
              value={value}
              onChange={(newValue, mentionedIds) => {
                setValue(newValue)
                setMentionedUserIds(mentionedIds)
              }}
              minHeight='120px'
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className='justify-content-between'>
        <Form.Check
          checked={urgent}
          onChange={(e) => {
            setUrgent(e.target.checked)
          }}
          type={'checkbox'}
          id={`default-${'checkbox'}`}
          label={
            <span style={{color: 'black'}}>
              <FormattedMessage
                id='policies.detail.notes.isUrgent'
                defaultMessage='Is this an urgent note?'
              />
            </span>
          }
        />
        <div className='d-flex align-items-center gap-1'>
          <Button variant='secondary' size='sm' onClick={onClose}>
            <FormattedMessage id='common.action.close' defaultMessage='Close' />
          </Button>
          <Button size='sm'
            variant='info'
            onClick={handleComment}
            disabled={loading || !value || loadingNotes}
          >
            <FormattedMessage id='common.action.saveChanges' defaultMessage='Save changes' />
          </Button>
        </div>
      </Modal.Footer>
      </Modal>
    </>
  )
}
